/**
 * Compatibility decoder for historical chatbot bodies that contain bare JSON NaN/Infinity
 * values. The live backend now emits `null`, but persisted/legacy responses can still arrive as
 * strings through older proxies. Only complete JSON value tokens are rewritten; quoted user text
 * and object keys are never touched.
 */
export function parseChatbotBody<T>(body: unknown): T | null {
  if (typeof body !== 'string') return body as T

  try {
    return JSON.parse(replaceNonFiniteJsonValues(body)) as T
  } catch {
    return null
  }
}

function replaceNonFiniteJsonValues(source: string): string {
  let result = ''
  let inString = false
  let escaped = false

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index]
    if (character === undefined) break

    if (inString) {
      result += character
      if (escaped) escaped = false
      else if (character === '\\') escaped = true
      else if (character === '"') inString = false
      continue
    }

    if (character === '"') {
      inString = true
      result += character
      continue
    }

    const token = nonFiniteTokenAt(source, index)
    if (token && hasJsonValueBoundaries(source, index, token.length)) {
      result += 'null'
      index += token.length - 1
      continue
    }

    result += character
  }

  return result
}

function nonFiniteTokenAt(source: string, index: number): 'NaN' | 'Infinity' | '-Infinity' | null {
  if (source.startsWith('-Infinity', index)) return '-Infinity'
  if (source.startsWith('Infinity', index)) return 'Infinity'
  if (source.startsWith('NaN', index)) return 'NaN'
  return null
}

function hasJsonValueBoundaries(source: string, index: number, length: number): boolean {
  const previous = previousNonWhitespace(source, index - 1)
  const next = nextNonWhitespace(source, index + length)
  return (previous === null || '[:,'.includes(previous)) && (next === null || ',]}'.includes(next))
}

function previousNonWhitespace(source: string, index: number): string | null {
  for (let cursor = index; cursor >= 0; cursor -= 1) {
    const character = source[cursor]
    if (character !== undefined && !/\s/u.test(character)) return character
  }
  return null
}

function nextNonWhitespace(source: string, index: number): string | null {
  for (let cursor = index; cursor < source.length; cursor += 1) {
    const character = source[cursor]
    if (character !== undefined && !/\s/u.test(character)) return character
  }
  return null
}
